/**
 * Modal de confirmação padronizado — substitui o `confirm()` nativo
 * (que quebra dark mode e a identidade premium da marca).
 *
 * 2 formas de usar:
 *
 * A) Componente controlado:
 *    const [pendingDel, setPendingDel] = useState<Item | null>(null);
 *    <ConfirmDialog
 *      open={!!pendingDel}
 *      title="Excluir cliente?"
 *      message={`Confirma excluir ${pendingDel?.nome}? Ação não pode ser desfeita.`}
 *      tone="danger"
 *      confirmLabel="Excluir"
 *      onConfirm={() => excluir.mutate(pendingDel!.id)}
 *      onClose={() => setPendingDel(null)}
 *    />
 *
 * B) Hook imperativo (substituto direto pro confirm()):
 *    const confirm = useConfirm();
 *    if (await confirm({ title: 'Excluir?', message: '...', tone: 'danger' })) {
 *      excluir.mutate();
 *    }
 */
import { useState, useCallback, createContext, useContext } from "react";
import type React from "react";
import { AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import { Modal } from "./Modal";

type Tone = "danger" | "warn" | "info" | "success";

export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  tone?: Tone;
  confirmLabel?: string;
  cancelLabel?: string;
}

const TONE_META: Record<Tone, { icon: typeof AlertTriangle; cor: string; btn: string }> = {
  danger: {
    icon: AlertTriangle,
    cor: "text-rose-600 bg-rose-50",
    btn: "bg-rose-600 hover:bg-rose-700 text-white",
  },
  warn: {
    icon: AlertTriangle,
    cor: "text-amber-600 bg-amber-50",
    btn: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  info: {
    icon: Info,
    cor: "text-blue-600 bg-blue-50",
    btn: "bg-primary text-primary-foreground hover:opacity-90",
  },
  success: {
    icon: CheckCircle2,
    cor: "text-emerald-600 bg-emerald-50",
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white",
  },
};

interface ControlledProps extends ConfirmOptions {
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  tone = "info",
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  onConfirm,
  onClose,
}: ControlledProps) {
  const meta = TONE_META[tone];
  const Icon = meta.icon;
  return (
    <Modal open={open} onClose={onClose} title="">
      <div className="flex gap-3 -mt-2">
        <div
          className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${meta.cor}`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">{title}</h3>
          {message && (
            <div className="text-sm text-muted-foreground mt-1.5">{message}</div>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm rounded-md hover:bg-muted"
        >
          {cancelLabel}
        </button>
        <button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className={`px-4 py-2 text-sm rounded-md font-medium ${meta.btn}`}
          autoFocus
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================================
// Hook imperativo: substituto direto pro confirm()
// ============================================================================
type Resolver = (ok: boolean) => void;

const ConfirmCtx = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{
    opts: ConfirmOptions;
    resolver: Resolver;
  } | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ opts, resolver: resolve });
    });
  }, []);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialog
          open
          {...state.opts}
          onConfirm={() => state.resolver(true)}
          onClose={() => {
            state.resolver(false);
            setState(null);
          }}
        />
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const fn = useContext(ConfirmCtx);
  if (!fn) {
    // Fallback: se Provider não estiver montado, usa confirm() nativo
    // pra não quebrar o app caso esqueçam de incluir o Provider.
    return (opts: ConfirmOptions): Promise<boolean> =>
      Promise.resolve(
        window.confirm(`${opts.title}${opts.message ? `\n\n${opts.message}` : ""}`)
      );
  }
  return fn;
}
